// EditAmazonShipmentPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { API_URL } from '../config';
import '../index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { parse, format } from 'date-fns';
import { MARKETPLACES } from './AmazonShipmentsPopup';

const EditAmazonShipmentPopup = ({ isOpen, onClose, onSubmit, shipmentData }) => {
  const [shipmentID, setShipmentID] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupDateFact, setPickupDateFact] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [skus, setSkus] = useState([]);
  const [boxesInput, setBoxesInput] = useState('');
  const [error, setError] = useState('');
  const [isCompleteChecked, setIsCompleteChecked] = useState(false);
  const [missingBoxes, setMissingBoxes] = useState([]);

  // Carrier & proof state
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrierID, setSelectedCarrierID] = useState('');
  const [requireProof, setRequireProof] = useState(false);
  const [proofFiles, setProofFiles] = useState([]);
  const [proofUploading, setProofUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchCarriers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (shipmentData) {
      setShipmentID(shipmentData.shipmentID);
      setPickupDate(shipmentData.pickupDate);
      setPickupDateFact(shipmentData.pickupDateFact || '');
      setMarketplace(shipmentData.marketplace || '');
      setIsCompleteChecked(false);
      setSkus(shipmentData.skus || []);
      fetchShipmentDetails().then(fetchBoxes);
      fetchBoxes();
      fetchProofFiles(shipmentData.shipmentID);
    }
  }, [shipmentData]);

  const fetchCarriers = async () => {
    try {
      const res = await api.get('/api/amz-carriers');
      setCarriers(res.data);
    } catch {
      // non-critical
    }
  };

  const fetchProofFiles = async (id) => {
    try {
      const res = await api.get(`/api/amz-shipments/${id}/proof`);
      setProofFiles(res.data);
    } catch {
      // non-critical
    }
  };

  const fetchBoxes = async () => {
    if (!shipmentData || !shipmentData.shipmentID) {
      setError('Shipment data is missing');
      return;
    }
    try {
      const response = await api.get('/api/amz-shipments-boxes', {
        params: { ShipmentID: shipmentData.shipmentID }
      });
      const boxesBySku = response.data.reduce((acc, box) => {
        acc[box.sku] = acc[box.sku] || [];
        acc[box.sku].push(box.box);
        return acc;
      }, {});
      setSkus(prevSkus => prevSkus.map(sku => ({
        ...sku,
        boxes: boxesBySku[sku.sku] || [],
        boxesAdded: boxesBySku[sku.sku]?.length || 0
      })));
    } catch {
      setError('Failed to load boxes');
    }
  };

  const fetchShipmentDetails = async () => {
    try {
      const response = await api.get(`/api/edit-amz-shipments/${shipmentData.shipmentID}`);
      const data = response.data;
      setSkus(prevSkus =>
        data.skus.map((newSku) => {
          const existingSku = prevSkus.find(s => s.sku === newSku.sku);
          return {
            ...newSku,
            expanded: existingSku ? existingSku.expanded : false,
            boxes: existingSku ? existingSku.boxes : []
          };
        })
      );
      setPickupDate(data.pickupDate);
      setSelectedCarrierID(data.carrier_id ? String(data.carrier_id) : '');
      setRequireProof(!!data.require_proof);
      if (data.proofFiles) setProofFiles(data.proofFiles);
    } catch {
      setError('Failed to load shipment details');
    }
  };

  const handleCarrierChange = async (e) => {
    const newCarrierID = e.target.value;
    setSelectedCarrierID(newCarrierID);
    const found = carriers.find(c => String(c.carrierID) === newCarrierID);
    setRequireProof(found ? !!found.require_proof : false);
    try {
      await api.put(`/api/amz-shipments/${shipmentID}/carrier`, {
        carrier_id: newCarrierID || null
      });
    } catch {
      // non-critical, will save on next submit
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProofUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/api/amz-shipments/${shipmentID}/proof`, formData);
      await fetchProofFiles(shipmentID);
    } catch {
      setError('Failed to upload proof file');
    } finally {
      setProofUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteProof = async (id) => {
    try {
      await api.delete(`/api/amz-shipments-proof/${id}`);
      setProofFiles(prev => prev.filter(f => f.id !== id));
    } catch {
      setError('Failed to delete proof file');
    }
  };

  const proofNeeded = requireProof && proofFiles.length === 0 && pickupDate && new Date(pickupDate) <= new Date();

  const parseScannedData = (data) => {
    const lines = data.trim().split('\n');
    const parsedEntries = lines.map((line, index) => {
      try {
        // Name is greedy (may contain commas), SKU is the last comma-free part before BOX#
        const match = line.match(
          /^(?:(\d{4}\/\d{2}\/\d{2}) (\d{2}:\d{2}:\d{2}) )?([A-Z]+-.+), ([^,]+), BOX#(.+?)\/(\d+)(?: (\d{4}\/\d{2}\/\d{2}) (\d{2}:\d{2}:\d{2}))?$/
        );
        if (!match) throw new Error(`Row ${index + 1}: Invalid format.`);
        let [, dateStart, timeStart, prefixSetName, sku, boxNumber, quantity, dateEnd, timeEnd] = match;
        const dateStr = dateStart || dateEnd;
        const time = timeStart || timeEnd;
        if (!dateStr || !time) throw new Error(`Row ${index + 1}: Missing date or time.`);
        const parsedDate = parse(dateStr, 'yyyy/MM/dd', new Date());
        if (isNaN(parsedDate)) throw new Error(`Row ${index + 1}: Invalid date format.`);
        const formattedDate = format(parsedDate, 'yyyy-MM-dd');
        const [prefix, ...setNameParts] = prefixSetName.split('-');
        const setName = setNameParts.join('-').trim();
        return {
          sku: sku.trim(), boxNumber: boxNumber.trim(),
          quantity: parseInt(quantity.trim(), 10),
          date: formattedDate, time: time.trim(),
          destination: prefix === 'SHIP' ? 'FBA' : 'Shipping Station',
          setName
        };
      } catch (err) {
        console.error(err.message);
        return null;
      }
    });
    const uniqueEntries = [];
    const seen = new Set();
    parsedEntries.forEach(entry => {
      if (entry) {
        const key = `${entry.sku}-${entry.boxNumber}`;
        if (!seen.has(key)) { seen.add(key); uniqueEntries.push(entry); }
      }
    });
    return uniqueEntries;
  };

  const handleAddBoxes = async () => {
    try {
      const parsedBoxes = parseScannedData(boxesInput);
      const validSkus = skus.map(s => s.sku);
      const boxesToAdd = parsedBoxes
        .filter(box => validSkus.includes(box.sku))
        .map(box => ({ sku: box.sku, box: box.boxNumber, quantity: box.quantity, setName: box.setName }));
      if (!boxesToAdd.length) { setError('No valid boxes found'); return; }
      const response = await api.post('/api/amz-shipments-boxes', { shipmentID, boxes: boxesToAdd });
      const { added, alreadyAdded, missing } = response.data;
      if (added.length > 0) { await fetchBoxes(); await fetchShipmentDetails(); }
      if (alreadyAdded.length > 0) {
        setError(alreadyAdded.map(b => `Box ${b.box} for SKU ${b.sku} is already added.`).join('\n'));
      } else { setError(''); }
      setMissingBoxes(missing.length > 0 ? missing : []);
      setBoxesInput('');
    } catch {
      setError('Failed to add boxes');
    }
  };

  const handleAddMissingBox = async (box) => {
    try {
      await api.post('/api/amz-shipments-boxes/add-anyway', {
        shipmentID, sku: box.sku, box: box.box, quantity: box.quantity
      });
      setMissingBoxes(prev => prev.filter(b => b.sku !== box.sku || b.box !== box.box));
      await fetchBoxes(); await fetchShipmentDetails();
    } catch (err) {
      if (err.response && err.response.status === 409) setError(err.response.data.error);
      else setError('Failed to add box');
    }
  };

  const handleAddAllMissingBoxes = async () => {
    for (const box of missingBoxes) await handleAddMissingBox(box);
  };

  const handleDeleteBox = async (sku, box) => {
    try {
      await api.delete('/api/amz-shipments-boxes', {
        params: { ShipmentID: shipmentID, sku, box }
      });
      await fetchBoxes(); await fetchShipmentDetails();
    } catch {
      setError('Failed to delete box');
    }
  };

  const handleSubmit = () => {
    if (!shipmentID || !pickupDate) { setError('ShipmentID and Pickup Date Plan are required'); return; }
    if (isCompleteChecked && !pickupDateFact) {
      setError('Pickup Date Fact is required before marking this shipment as complete.');
      return;
    }
    if (isCompleteChecked && proofNeeded) {
      setError('Proof of delivery is required before marking this shipment as complete.');
      return;
    }
    const updateData = {
      shipmentID, pickupDate, pickupDateFact, marketplace,
      skus: skus.map(sku => ({
        sku: sku.sku, asin: sku.asin,
        boxesNeeded: sku.boxesNeeded, setsNeeded: sku.setsNeeded,
        boxesAdded: sku.boxesAdded, setsAdded: sku.setsAdded
      })),
      statusID: isCompleteChecked ? 5 : null
    };
    onSubmit(updateData);
    onClose();
  };

  const handlePrint = () => {
    const rows = skus.map(s =>
      `<tr><td>${s.sku}</td><td>${s.name || ''}</td><td class="n">${s.boxesNeeded || 0}</td></tr>`
    ).join('');
    const total = skus.reduce((sum, s) => sum + (s.boxesNeeded || 0), 0);
    const w = window.open('', '_blank');
    if (!w) { setError('Popup blocked — allow popups to print'); return; }
    w.document.write(`<!DOCTYPE html>
<html><head><title>${shipmentID}</title><style>
  @page { margin: 12mm; }
  body { font-family: Arial, sans-serif; margin: 0; }
  table { border-collapse: collapse; width: 100%; font-size: 13px; }
  th, td { border: 1px solid #333; padding: 4px 8px; text-align: left; }
  th { background: #f3f4f6; }
  .n { font-weight: bold; color: #c00; }
  tfoot td { font-weight: bold; }
</style></head><body>
<table>
  <thead>
    <tr><th colspan="3" style="text-align:center;font-size:15px">${shipmentID}</th></tr>
    <tr><th>SKU</th><th>Name</th><th>Boxes Needed</th></tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot><tr><td>Total</td><td></td><td>${total}</td></tr></tfoot>
</table>
<script>
  window.onload = function () { window.print(); };
  window.onafterprint = function () { window.close(); };
<\/script>
</body></html>`);
    w.document.close();
  };

  const toggleRowExpansion = (index) => {
    setSkus(prev => prev.map((sku, i) => i === index ? { ...sku, expanded: !sku.expanded } : sku));
  };

  const getFileIconClass = (fileType) => {
    if (fileType.includes('pdf')) return 'fas fa-file-pdf';
    if (fileType.includes('image') || fileType.includes('jpg') || fileType.includes('jpeg') || fileType.includes('png') || fileType.includes('gif')) return 'fas fa-file-image';
    if (fileType.includes('word') || fileType.includes('doc')) return 'fas fa-file-word';
    return 'fas fa-file';
  };

  if (!isOpen) return null;

  return (
    <div className="amz-popup-overlay">
      <div className="amz-popup-content">
        <h2 className="amz-popup-title">Edit Shipment</h2>
        <p className="amz-popup-subtitle">Manage shipment details</p>
        <button className="amz-close-btn" onClick={onClose}><i className="fas fa-times" /></button>
        <button
          type="button"
          className="amz-close-btn"
          style={{ right: 64, color: '#5B965D' }}
          title="Print packing list"
          onClick={handlePrint}
        >
          <i className="fas fa-print" style={{ fontSize: 15, margin: 0 }} />
        </button>

        <form className="amz-form" onSubmit={e => e.preventDefault()}>
          <h3 className="amz-form-subtitle">Details</h3>
          <div className="amz-form-group">
            <label>ShipmentID *</label>
            <input type="text" value={shipmentID} readOnly />
          </div>
          <div className="amz-form-group">
            <label>Marketplace</label>
            <select
              value={marketplace}
              onChange={e => setMarketplace(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111' }}
            >
              <option value="">— Not set —</option>
              {MARKETPLACES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="amz-form-group">
            <label>Pickup Date Plan *</label>
            <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)} />
          </div>
          <div className="amz-form-group">
            <label>Pickup Date Fact{isCompleteChecked ? ' *' : ''}</label>
            <input type="date" value={pickupDateFact} onChange={e => setPickupDateFact(e.target.value)} />
          </div>

          {/* Carrier */}
          <div className="amz-form-group">
            <label>Carrier</label>
            <select
              value={selectedCarrierID}
              onChange={handleCarrierChange}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111' }}
            >
              <option value="">— No carrier —</option>
              {carriers.map(c => (
                <option key={c.carrierID} value={String(c.carrierID)}>
                  {c.name}{c.require_proof ? ' (Proof required)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Proof of Delivery */}
          {requireProof && (
            <div style={{
              margin: '4px 0 16px',
              borderRadius: 10,
              border: `1.5px solid ${proofNeeded ? '#f59e0b' : '#86efac'}`,
              background: proofNeeded ? '#fffbeb' : '#f0fdf4',
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                borderBottom: `1px solid ${proofNeeded ? '#fde68a' : '#bbf7d0'}`,
                background: proofNeeded ? '#fef3c7' : '#dcfce7'
              }}>
                <i
                  className={proofNeeded ? 'fas fa-triangle-exclamation' : 'fas fa-circle-check'}
                  style={{ fontSize: 14, color: proofNeeded ? '#d97706' : '#16a34a' }}
                />
                <span style={{ fontWeight: 600, fontSize: 13, color: proofNeeded ? '#92400e' : '#166534' }}>
                  {proofNeeded ? 'Proof of Delivery Required' : 'Proof of Delivery Provided'}
                </span>
              </div>

              {/* Files list */}
              {proofFiles.length > 0 && (
                <div style={{ padding: '8px 14px 4px' }}>
                  {proofFiles.map(f => (
                    <div key={f.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px', marginBottom: 6,
                      background: '#fff', borderRadius: 7,
                      border: '1px solid #e5e7eb'
                    }}>
                      <i
                        className={getFileIconClass(f.file_type)}
                        style={{ fontSize: 15, color: '#5B965D', flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {f.original_name}
                      </span>
                      <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ''}
                      </span>
                      {/* Preview button */}
                      <button
                        type="button"
                        onClick={() => setPreviewFile(f)}
                        title="Preview"
                        style={{
                          flexShrink: 0, width: 26, height: 26, borderRadius: 5,
                          border: '1px solid #e5e7eb', background: '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#9ca3af', transition: 'color 0.15s, border-color 0.15s, background 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; e.currentTarget.style.color = '#16a34a'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#9ca3af'; }}
                      >
                        <i className="fas fa-eye" style={{ fontSize: 11, margin: 0 }} />
                      </button>
                      {/* Download button */}
                      <a
                        href={`${API_URL}/uploads/proof/${f.file_name}`}
                        download={f.original_name}
                        title="Download"
                        style={{
                          flexShrink: 0, width: 26, height: 26, borderRadius: 5,
                          border: '1px solid #e5e7eb', background: '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#9ca3af', textDecoration: 'none', transition: 'color 0.15s, border-color 0.15s, background 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; e.currentTarget.style.color = '#16a34a'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#9ca3af'; }}
                      >
                        <i className="fas fa-download" style={{ fontSize: 11, margin: 0 }} />
                      </a>
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handleDeleteProof(f.id)}
                        title="Remove"
                        style={{
                          flexShrink: 0, width: 26, height: 26, borderRadius: 5,
                          border: '1px solid #e5e7eb', background: '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#9ca3af', transition: 'color 0.15s, border-color 0.15s, background 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#9ca3af'; }}
                      >
                        <i className="fas fa-trash" style={{ fontSize: 11, margin: 0 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload */}
              <div style={{ padding: '8px 14px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                  onChange={handleProofUpload}
                  style={{ display: 'none' }}
                  id="proof-upload"
                />
                <label
                  htmlFor="proof-upload"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 7,
                    background: proofUploading ? '#4e8350' : '#5B965D',
                    color: '#fff', fontSize: 13, fontWeight: 500,
                    cursor: proofUploading ? 'not-allowed' : 'pointer',
                    transition: 'background 0.15s'
                  }}
                >
                  <i className={proofUploading ? 'fas fa-spinner fa-spin' : 'fas fa-arrow-up-from-bracket'} style={{ fontSize: 12 }} />
                  {proofUploading ? 'Uploading...' : 'Upload File'}
                </label>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>JPG, PNG, PDF, DOC — max 20MB</span>
              </div>
            </div>
          )}

          <div className="amz-complete-checkbox">
            <label>
              <input
                type="checkbox"
                checked={isCompleteChecked}
                onChange={e => setIsCompleteChecked(e.target.checked)}
                disabled={proofNeeded}
              />
              Set this shipment as Complete
              {proofNeeded && (
                <span style={{ marginLeft: 8, color: '#d97706', fontSize: 12, fontWeight: 500 }}>
                  <i className="fas fa-triangle-exclamation" style={{ marginRight: 4, fontSize: 11 }} />
                  Upload proof of delivery first
                </span>
              )}
            </label>
          </div>

          <h3 className="amz-form-subtitle">SKUs</h3>
          <table className="amz-sku-table">
            <thead>
              <tr>
                <th></th>
                <th>SKU</th>
                <th>ASIN</th>
                <th>Name</th>
                <th>Boxes in Stock</th>
                <th>Boxes Needed</th>
                <th>Boxes Added</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <button type="button" onClick={() => toggleRowExpansion(index)}>
                        {sku.expanded ? '▲' : '▼'}
                      </button>
                    </td>
                    <td>{sku.sku}</td>
                    <td>{sku.asin}</td>
                    <td>{sku.name}</td>
                    <td>{sku.boxesStock}</td>
                    <td style={{ color: sku.boxesNeeded === sku.boxesAdded ? '#16a34a' : '#dc2626', fontWeight: '600' }}>{sku.boxesNeeded}</td>
                    <td style={{ color: sku.boxesNeeded === sku.boxesAdded ? '#16a34a' : '#dc2626', fontWeight: '600' }}>{sku.boxesAdded}</td>
                  </tr>
                  {sku.expanded && sku.boxes && (
                    <tr>
                      <td colSpan="7">
                        <table className="amz-sub-table">
                          <thead>
                            <tr><th>Box Number</th><th>Action</th></tr>
                          </thead>
                          <tbody>
                            {sku.boxes.map(box => (
                              <tr key={box}>
                                <td>{box}</td>
                                <td>
                                  <button type="button" onClick={() => handleDeleteBox(sku.sku, box)}>
                                    <i className="fas fa-trash" style={{ fontSize: 12, margin: 0 }} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #555' }}>
                <td></td>
                <td>Total</td>
                <td></td>
                <td></td>
                <td>{skus.reduce((sum, s) => sum + (s.boxesStock || 0), 0)}</td>
                <td>{skus.reduce((sum, s) => sum + (s.boxesNeeded || 0), 0)}</td>
                <td>{skus.reduce((sum, s) => sum + (s.boxesAdded || 0), 0)}</td>
              </tr>
            </tbody>
          </table>

          <div className="amz-boxes-section">
            <h3>Add Boxes to Shipment <span style={{ fontSize: '11px', fontWeight: '400', color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>(e.g. <code>SHIP-SetName, SKU-123, BOX#1/5 2024/01/15 10:30:00</code>)</span></h3>
            <textarea
              value={boxesInput}
              onChange={e => setBoxesInput(e.target.value)}
              placeholder="Scan boxes here..."
              rows="4"
            />
            <button type="button" onClick={handleAddBoxes}>Add to Shipment</button>
          </div>

          {error && <p className="amz-error">{error}</p>}

          {missingBoxes.length > 0 && (
            <div>
              <h4>These boxes are not in inventory:</h4>
              <ul>
                {missingBoxes.map((box, index) => (
                  <li key={index}>SKU: {box.sku}, Box: {box.box}</li>
                ))}
              </ul>
              <button className="amz-submit-anyway-btn" onClick={handleAddAllMissingBoxes}>Add all anyway</button>
            </div>
          )}

          <div className="amz-form-actions">
            <button type="button" className="amz-submit-btn" onClick={handleSubmit}>Save</button>
            <button type="button" className="amz-cancel-btn" onClick={onClose}>Close</button>
          </div>
        </form>
      </div>

      {/* Preview overlay */}
      {previewFile && (
        <div
          onClick={() => setPreviewFile(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 2000,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14,
              width: '100%', maxWidth: 860,
              maxHeight: '90vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 32px 80px rgba(0,0,0,0.4)'
            }}
          >
            {/* Preview header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 18px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafafa'
            }}>
              <i className={getFileIconClass(previewFile.file_type)} style={{ fontSize: 16, color: '#5B965D' }} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewFile.original_name}
              </span>
              <a
                href={`${API_URL}/uploads/proof/${previewFile.file_name}`}
                download={previewFile.original_name}
                title="Download"
                style={{
                  width: 32, height: 32, borderRadius: 7,
                  border: '1px solid #e5e7eb', background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6b7280', textDecoration: 'none', marginRight: 4,
                  transition: 'color 0.15s, background 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.color = '#16a34a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <i className="fas fa-download" style={{ fontSize: 13 }} />
              </a>
              <button
                type="button"
                onClick={() => setPreviewFile(null)}
                style={{
                  width: 32, height: 32, borderRadius: 7,
                  border: '1px solid #e5e7eb', background: '#fff',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6b7280', transition: 'color 0.15s, background 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <i className="fas fa-times" style={{ fontSize: 13 }} />
              </button>
            </div>

            {/* Preview body */}
            <div style={{ flex: 1, overflow: 'auto', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              {previewFile.file_type.includes('image') ||
               previewFile.file_type.includes('jpg') ||
               previewFile.file_type.includes('jpeg') ||
               previewFile.file_type.includes('png') ||
               previewFile.file_type.includes('gif') ? (
                <img
                  src={`${API_URL}/uploads/proof/${previewFile.file_name}`}
                  alt={previewFile.original_name}
                  style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 4 }}
                />
              ) : previewFile.file_type.includes('pdf') ? (
                <iframe
                  src={`${API_URL}/uploads/proof/${previewFile.file_name}`}
                  title={previewFile.original_name}
                  style={{ width: '100%', height: '75vh', border: 'none' }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
                  <i className="fas fa-file-lines" style={{ fontSize: 48, marginBottom: 16, display: 'block', color: '#d1d5db' }} />
                  <p style={{ margin: '0 0 16px', fontSize: 14 }}>Preview not available for this file type</p>
                  <a
                    href={`${API_URL}/uploads/proof/${previewFile.file_name}`}
                    download={previewFile.original_name}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 8,
                      background: '#5B965D', color: '#fff',
                      textDecoration: 'none', fontSize: 13, fontWeight: 500
                    }}
                  >
                    <i className="fas fa-download" style={{ fontSize: 12 }} />
                    Download file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAmazonShipmentPopup;
